import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  message,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { Notice } from '../../components/common/Notice';
import surveyAnalysisService, {
  parseVisibilityDeniedError,
  SurveyAnalysisResponse,
  ProfileField,
} from '../../services/surveyAnalysis.service';
import { surveyService } from '../../services/survey.service';
import { auditTrailService } from '../../services/audit-trail.service';
import { ComplementaryAnalysisResponse } from '../../types/analysis-complementary.types';
import { GeographicScope, Survey } from '../../types/survey.types';
import AnalysisSummaryView from '../../components/analytics/AnalysisSummaryView';
import AnalyticsSegmentedView from '../../components/analytics/AnalyticsSegmentedView';
import AnalyticsExplorer from '../../components/analytics/explorer/AnalyticsExplorer';
import { surveyAnalyticsService } from '../../services/surveyAnalytics.service';
import { RichTextDisplay } from '../../components/common/RichTextDisplay';
import { AiAnalysisWorkspace } from '../../components/analytics/ai';

const { Title, Text } = Typography;

const SEGMENT_OPTIONS: Array<{ value: ProfileField; label: string }> = [
  { value: 'gender', label: 'Gênero' },
  { value: 'age_group', label: 'Faixa Etária' },
  { value: 'education', label: 'Escolaridade' },
  { value: 'economic_level', label: 'Renda' },
  { value: 'state', label: 'Estado' },
];

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { color: string; label: string }> = {
    PENDING: { color: 'default', label: 'Aguardando processamento' },
    IN_PROGRESS: { color: 'processing', label: 'Processando...' },
    COMPLETED_UNPUBLISHED: { color: 'warning', label: 'Pronta para publicar' },
    PUBLISHED: { color: 'success', label: 'Publicada' },
  };
  const cfg = config[status] ?? { color: 'default', label: status };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
}

function formatPeriod(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) return '-';
  const start = new Date(startDate).toLocaleDateString('pt-BR');
  const end = new Date(endDate).toLocaleDateString('pt-BR');
  return `${start} a ${end}`;
}

function formatScope(survey?: Survey): string {
  if (!survey) return '-';
  if (survey.geographicScope === GeographicScope.NATIONAL) return 'Nacional';
  if (survey.geographicScope === GeographicScope.STATE) {
    return survey.state?.name ? `Estadual (${survey.state.name})` : 'Estadual';
  }
  return survey.city?.name ? `Municipal (${survey.city.name})` : 'Municipal';
}

export function AdminSurveyAnalysisPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [criterion, setCriterion] = useState<ProfileField>('gender');
  const [downloadingAttachment, setDownloadingAttachment] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const surveyIdNum = Number(surveyId);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['survey-analysis', surveyIdNum],
    queryFn: () => surveyAnalysisService.getAnalysis(surveyIdNum),
    refetchInterval: (query) => {
      const d = query.state.data as any;
      if (d?.status === 'PENDING' || d?.status === 'IN_PROGRESS') return 5000;
      return false;
    },
  });

  const { data: surveyDetails } = useQuery<Survey>({
    queryKey: ['survey', surveyIdNum],
    queryFn: () => surveyService.getSurvey(String(surveyIdNum)),
    enabled: Number.isFinite(surveyIdNum),
    staleTime: 60_000,
  });

  const { data: finalQuestions } = useQuery({
    queryKey: ['survey-analysis-final-v2-questions', surveyIdNum, (data as any)?.versionId],
    queryFn: () => surveyAnalyticsService.listQuestions(String(surveyIdNum), String((data as any)?.versionId)),
    enabled: Boolean((data as any)?.versionId && (data as any)?.analyticsMode === 'QUERY_API'),
  });

  const { data: complementaryData, isLoading: loadingComplementary } =
    useQuery<ComplementaryAnalysisResponse>({
      queryKey: ['survey-analysis-complementary', surveyIdNum],
      queryFn: () => surveyAnalysisService.getComplementaryAnalysis(surveyIdNum),
      enabled: Number.isFinite(surveyIdNum),
    });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    const denied = parseVisibilityDeniedError(error);
    if (denied) {
      return (
        <Notice
          type="warning"
          message="Acesso aos resultados bloqueado"
          description={`${denied.message} (código: ${denied.code})`}
          action={
            <Button onClick={() => refetch()} icon={<ReloadOutlined />}>
              Atualizar
            </Button>
          }
        />
      );
    }
    return (
      <Alert
        type="error"
        message="Erro ao carregar análise"
        description="Não foi possível carregar os dados da análise."
        action={
          <Button onClick={() => refetch()} icon={<ReloadOutlined />}>
            Tentar novamente
          </Button>
        }
      />
    );
  }

  const isPending = !data || data.status === 'PENDING' || data.status === 'IN_PROGRESS';

  if (isPending) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 py-12">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} />
          <Title level={4}>Análise em processamento</Title>
          <Text type="secondary">
            Os dados estão sendo processados. Esta página será atualizada automaticamente.
          </Text>
          <StatusBadge status={data?.status ?? 'PENDING'} />
        </div>
      </Card>
    );
  }

  const analysis = data as SurveyAnalysisResponse;
  const payload = analysis.payload;
  const questions = payload?.questions ?? [];
  const isQueryApiAnalysis = analysis.analyticsMode === 'QUERY_API' && Boolean(analysis.versionId);
  const complementaryContent = complementaryData?.content?.sanitizedHtml ?? '';
  const complementaryAttachment = complementaryData?.attachment ?? null;
  const isComplementaryPublished = complementaryData?.status === 'PUBLISHED';

  const handleDownloadAttachment = async () => {
    try {
      setDownloadingAttachment(true);
      const url = await auditTrailService.getComplementaryAttachmentDownloadUrl(surveyIdNum);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        messageApi.error('URL de download indisponível.');
      }
    } catch {
      messageApi.error('Erro ao obter link de download.');
    } finally {
      setDownloadingAttachment(false);
    }
  };

  return (
    <div className="space-y-4">
      {contextHolder}
      <Space style={{ marginBottom: 8 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/admin/surveys/${surveyId}`)}>
          Voltar para a pesquisa
        </Button>
      </Space>

      <Card title="Análise da Pesquisa">
        <Descriptions column={3} layout="vertical" size="small">
          <Descriptions.Item label="Status da Análise">
            <StatusBadge status={analysis.status} />
            {' '}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {analysis.status === 'PUBLISHED' && analysis.publishedAt
                ? `Publicada em ${new Date(analysis.publishedAt).toLocaleString('pt-BR')}`
                : payload
                  ? `Gerada em ${new Date(payload.generated_at).toLocaleString('pt-BR')}`
                  : null}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Respondentes">
            {payload?.total_respondents ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Nome">{surveyDetails?.name ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Período">
            {formatPeriod(surveyDetails?.startDate, surveyDetails?.endDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Abrangência">{formatScope(surveyDetails)}</Descriptions.Item>
          <Descriptions.Item label="Descrição" span={3}>
            {surveyDetails?.description ?? '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {!payload && !isQueryApiAnalysis && (
        <Notice
          type="warning"
          message="Análise sem dados"
          description="A análise foi processada mas o payload não está disponível."
        />
      )}

      {isQueryApiAnalysis && finalQuestions && (
        <AnalyticsExplorer
          surveyId={String(surveyIdNum)}
          versions={[{
            versionId: analysis.versionId as any,
            surveyId: String(surveyIdNum),
            kind: 'FINAL',
            finalAnalysisId: String(analysis.surveyId),
            origin: 'NATIVE_SNAPSHOT',
            status: 'COMPLETED',
            requestedAt: analysis.generatedAt ?? new Date().toISOString(),
            cutAt: null,
            generatedAt: analysis.generatedAt,
            participantCount: null,
            calculationVersion: 'statistics-v2',
            capabilities: { ranking: true, singleDimension: true, combinedFilters: false, pivot: false, export: false, compare: false },
          }]}
          questions={finalQuestions.items}
          initialVersionId={analysis.versionId ?? undefined}
          initialQuestionId={finalQuestions.items[0]?.questionId}
        />
      )}

      {payload && (
        <>
          <Tabs
            defaultActiveKey="summary"
            items={[
              {
                key: 'summary',
                label: 'Resumo Geral',
                children: <AnalysisSummaryView questions={questions} />,
              },
              {
                key: 'segmented',
                label: 'Por Perfil',
                children: (
                  <div className="space-y-4">
                    <Card size="small">
                      <div className="flex items-center gap-3">
                        <Text strong>Segmentar por:</Text>
                        <Select
                          value={criterion}
                          onChange={(v) => setCriterion(v)}
                          options={SEGMENT_OPTIONS}
                          style={{ width: 180 }}
                        />
                      </div>
                    </Card>
                    <AnalyticsSegmentedView questions={questions} criterion={criterion} />
                  </div>
                ),
              },
            ]}
          />

          <AiAnalysisWorkspace surveyId={String(surveyId)} />

          <Card title="Análise Complementar" style={{ marginTop: 16 }}>
            {loadingComplementary ? (
              <Spin />
            ) : isComplementaryPublished && complementaryContent ? (
              <div className="space-y-3">
                <RichTextDisplay html={complementaryContent} />
                {complementaryAttachment && (
                  <Button
                    icon={<DownloadOutlined />}
                    loading={downloadingAttachment}
                    onClick={handleDownloadAttachment}
                  >
                    Baixar PDF complementar
                  </Button>
                )}
              </div>
            ) : (
              <Text type="secondary">Análise complementar ainda não publicada.</Text>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default AdminSurveyAnalysisPage;
